import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, CreditCard, Zap, Building2, Crown, RefreshCw, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API}/api/payments/plans`),
        axios.get(`${API}/api/payments/subscription`, { headers })
      ]);

      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const token = localStorage.getItem('grc_token');
      const successUrl = `${window.location.origin}/dashboard?checkout=success`;
      const cancelUrl = `${window.location.origin}/billing?checkout=cancelled`;

      const res = await axios.post(`${API}/api/payments/checkout`, null, {
        params: { plan_id: planId, success_url: successUrl, cancel_url: cancelUrl },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Checkout failed');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'trial': return <Zap className="h-6 w-6" />;
      case 'starter': return <CreditCard className="h-6 w-6" />;
      case 'professional': return <Building2 className="h-6 w-6" />;
      case 'enterprise': return <Crown className="h-6 w-6" />;
      default: return <CreditCard className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'trial': return 'border-gray-200';
      case 'starter': return 'border-blue-200';
      case 'professional': return 'border-purple-200 ring-2 ring-purple-100';
      case 'enterprise': return 'border-yellow-200';
      default: return 'border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and payment details</p>
      </div>

      {/* Current Subscription */}
      <Card data-testid="current-subscription">
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Your active subscription details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getPlanIcon(subscription?.tier)}
              <div>
                <p className="text-xl font-semibold capitalize">{subscription?.name || 'Free Trial'}</p>
                <p className="text-sm text-muted-foreground">
                  {subscription?.tier === 'trial' 
                    ? 'Free trial - limited features'
                    : `$${plans.find(p => p.id === subscription?.tier)?.price || 0}/month`
                  }
                </p>
              </div>
            </div>
            <Badge className={subscription?.tier === 'trial' ? 'bg-gray-100' : 'bg-green-100 text-green-800'}>
              {subscription?.tier === 'trial' ? 'Trial' : 'Active'}
            </Badge>
          </div>

          {subscription?.limits && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Users</p>
                <p className="text-lg font-semibold">{subscription.limits.users || 'Unlimited'}</p>
              </div>
              <div className="p-3 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Frameworks</p>
                <p className="text-lg font-semibold">{subscription.limits.frameworks || 'Unlimited'}</p>
              </div>
              <div className="p-3 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Integrations</p>
                <p className="text-lg font-semibold">{subscription.limits.integrations || 'Unlimited'}</p>
              </div>
              <div className="p-3 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Storage</p>
                <p className="text-lg font-semibold">{subscription.limits.storage || 'Unlimited'}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative ${getPlanColor(plan.id)}`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.id === 'professional' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-purple-600">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  {getPlanIcon(plan.id)}
                  <CardTitle>{plan.name}</CardTitle>
                </div>
                <CardDescription>
                  <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features?.slice(0, 6).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.id === subscription?.tier ? 'outline' : 'default'}
                  disabled={plan.id === subscription?.tier || checkoutLoading === plan.id}
                  onClick={() => handleCheckout(plan.id)}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {checkoutLoading === plan.id ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : plan.id === subscription?.tier ? (
                    'Current Plan'
                  ) : plan.price === 0 ? (
                    'Start Trial'
                  ) : (
                    'Upgrade'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-medium">Can I change plans anytime?</p>
            <p className="text-sm text-muted-foreground">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <p className="font-medium">What payment methods do you accept?</p>
            <p className="text-sm text-muted-foreground">
              We accept all major credit cards through our secure Stripe payment processor.
            </p>
          </div>
          <div>
            <p className="font-medium">Is there a free trial?</p>
            <p className="text-sm text-muted-foreground">
              Yes, all new accounts start with a free trial that includes limited access to core features.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
